import React from "react";
import { useQuery } from "@tanstack/react-query";
import {
  User, Mail, Briefcase, Building2, BadgeCheck,
  CalendarDays, ShieldCheck,
} from "lucide-react";
import { backendApi } from "./api/backendApi";

const T = {
  green: '#1A3C2B',
  greenDeep: '#122B1F',
  greenMid: '#255238',
  greenTint: '#EDF3EF',
  greenAccent: '#2E7D52',
  bg: '#F6F6F3',
  card: '#FFFFFF',
  line: '#E7E6E0',
  lineSoft: '#F0EFEA',
  ink: '#15181C',
  body: '#3F4650',
  muted: '#6B7280',
  faint: '#9CA3AF',
};

export default function Profile() {
  const cached = backendApi.auth.getStoredUser();

  const { data: user = cached } = useQuery({
    queryKey: ["my-profile"],
    queryFn: () => backendApi.auth.me(),
    initialData: cached,
  });

  const displayName = user?.full_name || user?.name || "User";
  const initial = displayName.charAt(0).toUpperCase();
  const roleLabel = {
    admin: "Admin",
    hr: "HR",
    manager: "Reporting Manager",
    employee: "Employee",
  }[user?.role] || user?.role || "Employee";

  const joinDate = user?.join_date
    ? new Date(user.join_date).toLocaleDateString("en-US", {
        year: "numeric", month: "long", day: "numeric",
      })
    : null;

  // Sidebar info rows
  const infoRows = [
    { label: "Employee ID", value: user?.employee_id || "—", icon: BadgeCheck },
    { label: "Department", value: user?.department || "—", icon: Building2 },
    { label: "Position", value: user?.position || "—", icon: Briefcase },
    { label: "Role", value: roleLabel, icon: ShieldCheck },
    { label: "Email", value: user?.email || "—", icon: Mail },
    ...(joinDate ? [{ label: "Joined", value: joinDate, icon: CalendarDays }] : []),
  ];

  return (
    <div style={{ background: T.bg, minHeight: "100%" }}>

      {/* ── Cover band ─────────────────────────────────────────── */}
      <div
        style={{
          background: `linear-gradient(135deg, ${T.greenDeep} 0%, ${T.greenMid} 60%, ${T.greenAccent} 100%)`,
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Geometric accent shapes */}
        <div style={{
          position: "absolute", top: -60, right: -60,
          width: 300, height: 300,
          borderRadius: "50%",
          background: "rgba(255,255,255,0.04)",
          pointerEvents: "none",
        }} />
        <div style={{
          position: "absolute", bottom: -40, left: "30%",
          width: 200, height: 200,
          borderRadius: "50%",
          background: "rgba(255,255,255,0.03)",
          pointerEvents: "none",
        }} />

        <div style={{ padding: "48px 40px 40px", position: "relative", zIndex: 1 }}>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 28, flexWrap: "wrap" }}>

            {/* Avatar */}
            <div style={{
              width: 96, height: 96,
              borderRadius: 24,
              background: "rgba(255,255,255,0.12)",
              border: "2px solid rgba(255,255,255,0.25)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 38, fontWeight: 800, color: "#fff",
              flexShrink: 0,
              backdropFilter: "blur(8px)",
            }}>
              {initial}
            </div>

            {/* Name + role */}
            <div style={{ flex: 1, minWidth: 0, paddingBottom: 4 }}>
              <div style={{
                fontSize: 28, fontWeight: 800, color: "#fff",
                lineHeight: 1.15, letterSpacing: "-0.5px",
              }}>
                {displayName}
              </div>
              <div style={{
                fontSize: 14, color: "rgba(255,255,255,0.65)",
                marginTop: 4, display: "flex", alignItems: "center", gap: 8,
              }}>
                <Mail style={{ width: 13, height: 13, opacity: 0.6 }} />
                {user?.email || "—"}
              </div>
              <span style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                marginTop: 12,
                background: "rgba(255,255,255,0.15)",
                border: "1px solid rgba(255,255,255,0.22)",
                borderRadius: 100,
                padding: "4px 14px",
                fontSize: 11, fontWeight: 700,
                color: "#fff",
                letterSpacing: "0.06em",
                backdropFilter: "blur(6px)",
              }}>
                <ShieldCheck style={{ width: 12, height: 12 }} />
                {roleLabel}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Content area ───────────────────────────────────────── */}
      <div style={{ padding: "32px 40px 48px" }}>
        {/* minmax(0, 1fr) prevents the left track from growing past its
            fair share to fit its content — this is what forces the
            340px right column to render at an identical width to Settings. */}
        <div style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) 340px",
          gap: 24,
          alignItems: "start",
        }}>

          {/* ── Left: Info cards grid ─────────────────────────── */}
          <div style={{
            background: T.card,
            borderRadius: 16,
            border: `1px solid ${T.line}`,
            overflow: "hidden",
            minWidth: 0,
          }}>
            {/* Card header */}
            <div style={{
              padding: "18px 24px",
              borderBottom: `1px solid ${T.lineSoft}`,
              display: "flex", alignItems: "center", gap: 10,
            }}>
              <div style={{
                width: 32, height: 32,
                borderRadius: 10,
                background: T.greenTint,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <User style={{ width: 16, height: 16, color: T.green }} />
              </div>
              <span style={{ fontSize: 13, fontWeight: 700, color: T.ink }}>Account Information</span>
            </div>

            {/* Info rows */}
            <div style={{ padding: "8px 8px" }}>
              {infoRows.map((row, i) => (
                <div key={row.label} style={{
                  display: "flex", alignItems: "center", gap: 14,
                  padding: "14px 16px",
                  borderRadius: 10,
                  borderBottom: i < infoRows.length - 1 ? `1px solid ${T.lineSoft}` : "none",
                }}>
                  <div style={{
                    width: 38, height: 38, borderRadius: 10,
                    background: T.greenTint,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0,
                  }}>
                    <row.icon style={{ width: 17, height: 17, color: T.green }} strokeWidth={2} />
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{
                      fontSize: 10, fontWeight: 600,
                      color: T.faint,
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      marginBottom: 2,
                    }}>
                      {row.label}
                    </div>
                    <div style={{
                      fontSize: 14, fontWeight: 600, color: T.ink,
                      wordBreak: "break-word",
                    }}>
                      {row.value}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ── Right: Summary sidebar ────────────────────────── */}
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

            {/* Identity summary card */}
            <div style={{
              background: T.card,
              borderRadius: 16,
              border: `1px solid ${T.line}`,
              padding: "24px",
              textAlign: "center",
            }}>
              {/* Large initial */}
              <div style={{
                width: 72, height: 72,
                borderRadius: 20,
                background: T.green,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 30, fontWeight: 800, color: "#fff",
                margin: "0 auto 16px",
              }}>
                {initial}
              </div>
              <div style={{ fontSize: 17, fontWeight: 800, color: T.ink, lineHeight: 1.2 }}>
                {displayName}
              </div>
              <div style={{ fontSize: 12, color: T.muted, marginTop: 4 }}>
                {user?.position || roleLabel}
              </div>
              <div style={{
                marginTop: 16,
                padding: "12px",
                background: T.greenTint,
                borderRadius: 12,
                display: "flex", flexDirection: "column", gap: 6,
              }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <span style={{ fontSize: 11, color: T.muted }}>Employee ID</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: T.green }}>
                    {user?.employee_id || "—"}
                  </span>
                </div>
                <div style={{ height: 1, background: T.line }} />
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <span style={{ fontSize: 11, color: T.muted }}>Department</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: T.ink }}>
                    {user?.department || "—"}
                  </span>
                </div>
              </div>
            </div>

            {/* Status card */}
            <div style={{
              background: T.green,
              borderRadius: 16,
              padding: "20px 24px",
            }}>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.6)", fontWeight: 600, letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: 8 }}>
                Account Status
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{
                  width: 8, height: 8, borderRadius: "50%",
                  background: "#4ADE80",
                  boxShadow: "0 0 0 3px rgba(74,222,128,0.25)",
                }} />
                <span style={{ fontSize: 14, fontWeight: 700, color: "#fff" }}>Active</span>
              </div>
              <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid rgba(255,255,255,0.12)" }}>
                <div style={{ fontSize: 11, color: "rgba(255,255,255,0.5)", marginBottom: 2 }}>Role</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>{roleLabel}</div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}